import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "projects-overview"
    }}>{`Projects Overview`}</h1>
    <p>{`The CJA APIs allow you to retrieve, update, or create projects programmatically through Adobe Developer. The APIs use the same data and methods that are used when working with projects in the UI. For more information on understanding project definition data architecture, see the `}<a parentName="p" {...{
        "href": "structure.md"
      }}>{`Project Definition`}</a>{` article that accompanies this user guide.  See `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/analytics-platform/using/cja-workspace/home.html"
      }}>{`Workspace projects overview`}</a>{` in the CJA documentation for more information.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Adobe may add optional request and response members (name/value pairs) to existing API objects at any time and without notice or changes in versioning. Adobe recommends that you refer to the API documentation of any third-party tool you integrate with our APIs so that such additions are ignored in processing if not understood. If implemented properly, such additions are non-breaking changes for your implementation. Adobe will not remove parameters or add required parameters without first providing standard notification through release notes.`}</p>
    <h2 {...{
      "id": "best-practices"
    }}>{`Best Practices`}</h2>
    <p>{`Please follow these guidelines when using the Projects APIs:`}</p>
    <ul>
      <li parentName="ul">{`Make multiple, smaller requests instead of a large, single request.`}</li>
      <li parentName="ul">{`Request data once and cache it.`}</li>
      <li parentName="ul">{`Use caution when updating a project so that you do not alter the original project used by others.`}</li>
      <li parentName="ul">{`Avoid creating duplicate projects with the same definition. Creating many projects will affect performance for your company in some situations.`}</li>
      <li parentName="ul">{`If possible use the Analytics UI for creating projects from scratch. The UI offers significant advantages for managing and optimizing the complexity of this task. If you need to create projects programmatically, it is usually easier to create a template project in the UI and then have your application change only small portions of the project definition.`}</li>
    </ul>
    <h2 {...{
      "id": "creating-projects"
    }}>{`Creating Projects`}</h2>
    <p>{`As mentioned in the `}<a parentName="p" {...{
        "href": "#best-practices"
      }}>{`Best Practices`}</a>{` section above, the CJA UI is recommended for creating projects from scratch. If you do choose to use the API to create projects, keep in mind that certain fields cannot be supplied via the `}<inlineCode parentName="p">{`POST /projects`}</inlineCode>{` endpoint. For example, `}<inlineCode parentName="p">{`tags`}</inlineCode>{` are not stored within the projects themselves and will therefore be ignored if supplied on a creation request.`}</p>
    <h2 {...{
      "id": "projects-endpoint-description"
    }}>{`/projects Endpoint Description`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`/projects`}</inlineCode>{` endpoint description is shown in our `}<a parentName="p" {...{
        "href": "../../api.md"
      }}>{`Swagger UI`}</a>{`. Use the Swagger UI to see endpoint summaries, available methods, parameters, example values, models, and status codes, and to try out the API.`}</p>
    <h3 {...{
      "id": "expansions"
    }}>{`Expansions`}</h3>
    <p>{`Projects endpoints support the URL query parameter `}<inlineCode parentName="p">{`expansion`}</inlineCode>{`. This parameter allows specifying additional data fields to be populated in response objects. Available expansions are shown in the `}<a parentName="p" {...{
        "href": "../../api.md"
      }}>{`Swagger UI`}</a>{` description. The following table describes expansion fields:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Field`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`ownerFullName`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The name of the owner of the project`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`modified`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The date the project was last modified`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`tags`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Tags applied to the project`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`shares`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Shares applied to the project`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`accessLevel`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Access level the current user has to a particular project (Edit/Duplicate/View)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`externalReferences`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Analytics components (Segments, Calculated Metrics, Date Views) used in the project definition`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`definition`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The `}<a parentName="td" {...{
              "href": "structure.md"
            }}>{`project definition`}</a></td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "includetypes"
    }}>{`IncludeTypes`}</h3>
    <p>{`The GET multiple `}<inlineCode parentName="p">{`/projects`}</inlineCode>{` endpoint supports the query parameter `}<inlineCode parentName="p">{`includeType`}</inlineCode>{`. This parameter alters the set of projects that are included in API responses. By default, responses without this parameter include only projects owned by the user making the request. When using this parameter, the following values are possible:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`all`}</inlineCode>{`: Returns all projects linked to this company - this includeType is only available to admin users`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`shared`}</inlineCode>{`: Returns projects shared with the user`}</li>
    </ul>
    <h3 {...{
      "id": "locale"
    }}>{`Locale`}</h3>
    <p>{`Project endpoints support the URL query parameter `}<inlineCode parentName="p">{`locale`}</inlineCode>{`. Supported values are `}<inlineCode parentName="p">{`en_US`}</inlineCode>{`, `}<inlineCode parentName="p">{`fr_FR`}</inlineCode>{`, `}<inlineCode parentName="p">{`jp_JP`}</inlineCode>{`, `}<inlineCode parentName="p">{`ja_JP`}</inlineCode>{`, `}<inlineCode parentName="p">{`de_DE`}</inlineCode>{`, `}<inlineCode parentName="p">{`es_ES`}</inlineCode>{`, `}<inlineCode parentName="p">{`ko_KR`}</inlineCode>{`, `}<inlineCode parentName="p">{`pt_BR`}</inlineCode>{`, `}<inlineCode parentName="p">{`zh_CN`}</inlineCode>{`, and `}<inlineCode parentName="p">{`zh_TW`}</inlineCode>{`. This query parameter specifies the language for responses.`}</p>
    <h3 {...{
      "id": "pagination"
    }}>{`Pagination`}</h3>
    <p>{`Any response that can return multiple projects can be paginated with the `}<inlineCode parentName="p">{`page`}</inlineCode>{` and `}<inlineCode parentName="p">{`limit`}</inlineCode>{` URL query parameters. The `}<inlineCode parentName="p">{`limit`}</inlineCode>{` parameter indicates the size of the desired page, and the `}<inlineCode parentName="p">{`page`}</inlineCode>{` parameter indicates which page you want. A maximum page size of 1000 is enforced.`}</p>
    <h3 {...{
      "id": "example-curl-request"
    }}>{`Example cURL Request`}</h3>
    <p>{`The following example requests all projects on page 0 with page size 10:`}</p>
    <p><inlineCode parentName="p">{`GET https://cja.adobe.io/projects`}</inlineCode></p>
    <h3 {...{
      "id": "example-response"
    }}>{`Example Response`}</h3>
    <p>{`The following response 10 projects owned by the caller on the first page:`}</p>
    <pre><code parentName="pre" {...{}}>{`
{
  "content": [
    {
      "id": "6091a10005c7706c0acdd751",
      "name": "New Project",
      "description": "",
      "dataId": "dv_000000000000000000",
      "owner": {
        "imsUserId": "0000000000000000000@AdobeID"
      },
      "type": "project",
      "created": "2021-05-04T19:31:12Z"
    },
    {
      "id": "6094a7e01936af351300b81b",
      "name": "New Project",
      "description": "",
      "dataId": "dv_000000000000000000",
      "owner": {
        "imsUserId": "0000000000000000000@AdobeID"
      },
      "type": "project",
      "created": "2021-05-07T02:37:20Z"
    },
    {
      "id": "6094aa6b318ae31bf7d0a052",
      "name": "New Project",
      "description": "",
      "dataId": "dv_000000000000000000",
      "owner": {
        "imsUserId": "0000000000000000000@AdobeID"
      },
      "type": "project",
      "created": "2021-05-07T02:48:11Z"
    },
    {
      "id": "609c001c17d70a6b31694b7b",
      "name": "New Project",
      "description": "",
      "dataId": "dv_000000000000000000",
      "owner": {
        "imsUserId": "0000000000000000000@AdobeID"
      },
      "type": "project",
      "created": "2021-05-12T16:19:40Z"
    },
    {
      "id": "609d94557a1bac32bfe822d6",
      "name": "New Project",
      "description": "",
      "dataId": "dv_000000000000000000",
      "owner": {
        "imsUserId": "0000000000000000000@AdobeID"
      },
      "type": "project",
      "created": "2021-05-13T21:04:21Z"
    },
    {
      "id": "609d9b7117d70a6b31699d63",
      "name": "New Project",
      "description": "",
      "dataId": "dv_000000000000000000",
      "owner": {
        "imsUserId": "0000000000000000000@AdobeID"
      },
      "type": "project",
      "created": "2021-05-13T21:34:41Z"
    },
    {
      "id": "609eb9772ecede2233067197",
      "name": "New Project",
      "description": "",
      "dataId": "dv_000000000000000000",
      "owner": {
        "imsUserId": "0000000000000000000@AdobeID"
      },
      "type": "project",
      "created": "2021-05-14T17:55:03Z"
    },
    {
      "id": "60a400cfad861025de7edf25",
      "name": "New Project",
      "description": "",
      "dataId": "dv_000000000000000000",
      "owner": {
        "imsUserId": "0000000000000000000@AdobeID"
      },
      "type": "project",
      "created": "2021-05-18T18:00:47Z"
    },
    {
      "id": "60a403292ecede223306ffe3",
      "name": "New Project",
      "description": "",
      "dataId": "dv_000000000000000000",
      "owner": {
        "imsUserId": "0000000000000000000@AdobeID"
      },
      "type": "project",
      "created": "2021-05-18T18:10:49Z"
    },
    {
      "id": "60a5777dad861025de7ee116",
      "name": "New Project",
      "description": "",
      "dataId": "dv_000000000000000000",
      "owner": {
        "imsUserId": "0000000000000000000@AdobeID"
      },
      "type": "project",
      "created": "2021-05-19T20:39:25Z"
    }
  ],
  "totalPages": 10,
  "totalElements": 94,
  "number": 0,
  "numberOfElements": 10,
  "firstPage": true,
  "lastPage": false,
  "sort": [
    {
      "direction": "ASC",
      "property": "id",
      "ignoreCase": false,
      "ascending": true
    }
  ],
  "size": 10
}

`}</code></pre>
    <h2 {...{
      "id": "retrieving-a-single-project"
    }}>{`Retrieving a Single Project`}</h2>
    <p>{`You can retrieve projects individually if you know the project `}<inlineCode parentName="p">{`id`}</inlineCode>{`. To find the project `}<inlineCode parentName="p">{`id`}</inlineCode>{`, you can refer to the report descriptions or find it by using the multiple projects endpoint.`}</p>
    <h3 {...{
      "id": "example-request"
    }}>{`Example Request`}</h3>
    <p>{`The following example requests data on the project `}<inlineCode parentName="p">{`6091a10005c7706c0acdd751`}</inlineCode>{`:`}</p>
    <p><inlineCode parentName="p">{`GET https://cja.adobe.io/projects/6091a10005c7706c0acdd751`}</inlineCode></p>
    <h3 {...{
      "id": "example-response-1"
    }}>{`Example Response`}</h3>
    <p>{`The following example shows the response data for the request on project `}<inlineCode parentName="p">{`6091a10005c7706c0acdd751`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{}}>{`{
  "id": "6091a10005c7706c0acdd751",
  "name": "New Project",
  "description": "",
  "dataId": "dv_000000000000000000",
  "owner": {
    "imsUserId": "0000000000000000000@AdobeID"
  },
  "type": "project",
  "created": "2021-05-04T19:31:12Z"
}
`}</code></pre>
    <h2 {...{
      "id": "deleting-a-project"
    }}>{`Deleting a Project`}</h2>
    <p>{`If you delete a project, it is hidden from all users in all menus. Additionally, it is no longer returned when retrieving multiple projects in an API call.`}</p>
    <h3 {...{
      "id": "example-request-1"
    }}>{`Example Request`}</h3>
    <p>{`The following example deletes project `}<inlineCode parentName="p">{`6091a10005c7706c0acdd751`}</inlineCode>{`:`}</p>
    <p><inlineCode parentName="p">{`DELETE https://cja.adobe.io/projects/6091a10005c7706c0acdd751`}</inlineCode></p>
    <h3 {...{
      "id": "example-response-2"
    }}>{`Example Response`}</h3>
    <p>{`The following response shows the deletion status for project `}<inlineCode parentName="p">{`6091a10005c7706c0acdd751`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{}}>{`{
  "result": "success"
}
`}</code></pre>
    <h2 {...{
      "id": "updating-a-project"
    }}>{`Updating a Project`}</h2>
    <p>{`You can edit existing projects with the `}<inlineCode parentName="p">{`PUT /projects/{id}`}</inlineCode>{` endpoint. Some fields cannot be edited on a project with the PUT endpoint, including `}<inlineCode parentName="p">{`tags`}</inlineCode>{` and `}<inlineCode parentName="p">{`shares`}</inlineCode>{`. Other fields, including `}<inlineCode parentName="p">{`owner`}</inlineCode>{`, `}<inlineCode parentName="p">{`name`}</inlineCode>{`, `}<inlineCode parentName="p">{`description`}</inlineCode>{`, `}<inlineCode parentName="p">{`dataId`}</inlineCode>{`, and `}<inlineCode parentName="p">{`definition`}</inlineCode>{`, can be edited.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`PUT`}</inlineCode>{` endpoint also supports partial updates. This means that instead of sending the entire JSON object to the API, you can simply send fields that you want to update. For example, if you only want to update the name, use the JSON `}<inlineCode parentName="p">{`{"name":"Updated name"}`}</inlineCode>{`. The entire object is returned in the response, modified by the requested expansions.`}</p>
    <h3 {...{
      "id": "example-request-2"
    }}>{`Example Request`}</h3>
    <p>{`The following example updates the project `}<inlineCode parentName="p">{`6091a10005c7706c0acdd751`}</inlineCode>{` with a new name:`}</p>
    <p><inlineCode parentName="p">{`PUT https://cja.adobe.io/projects/6091a10005c7706c0acdd751`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`{"name":"{NAME UPDATE}"}
`}</code></pre>
    <h3 {...{
      "id": "example-response-3"
    }}>{`Example Response`}</h3>
    <p>{`The following example shows the response with updated name data:`}</p>
    <pre><code parentName="pre" {...{}}>{`
{
  "id": "6091a10005c7706c0acdd751",
  "name": "{NAME UPDATE}",
  "description": "",
  "dataId": "dv_000000000000000000",
  "owner": {
    "imsUserId": "0000000000000000000@AdobeID"
  },
  "type": "project",
  "created": "2021-05-04T19:31:12Z"
}

`}</code></pre>
    <h2 {...{
      "id": "validate-endpoint"
    }}>{`Validate Endpoint`}</h2>
    <p>{`Because data views can have different configurations, variables, or metrics, one project that is valid in one report suite may not be valid in another. To determine which projects to use in different data views, you can use the `}<inlineCode parentName="p">{`validate`}</inlineCode>{` endpoint. This endpoint allows you to `}<inlineCode parentName="p">{`POST`}</inlineCode>{` a definition along with a target `}<inlineCode parentName="p">{`dataId`}</inlineCode>{`. The validate endpoint responds with compatibility information on the project.`}</p>
    <h3 {...{
      "id": "example-request-3"
    }}>{`Example Request`}</h3>
    <p>{`The following example requests validation for the project with `}<inlineCode parentName="p">{`dataId`}</inlineCode>{`: `}<inlineCode parentName="p">{`dv_000000000000000000`}</inlineCode>{`. The example shows a `}<inlineCode parentName="p">{`POST`}</inlineCode>{` of the JSON `}<a parentName="p" {...{
        "href": "structure.md"
      }}>{`definition`}</a>{`:`}</p>
    <p><inlineCode parentName="p">{`POST https://cja.adobe.io/projects/validate`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`{
  "project": {
    "id": "6091a10005c7706c0acdd751",
    "name": "New Project",
    "description": "",
    "dataId": "dv_000000000000000000",
    "owner": {
      "id": 622291
    },
    "type": "project",
    "definition": {
      "additionalCuratedComponents": [],
      "colorScheme": {
        "id": "default",
        "label": "",
        "value": ["#00C0C7", "#5144D3", "#E8871A", "#DA3490", "#9089FA", "#47E26F", "#2780EB", "#6F38B1", "#DFBF03", "#CB6F10", "#268D6C", "#9BEC54", "#5EABFA", "#BE40CC", "#F56BB7", "#FEE02D"]
      },
      "countRepeatInstances": true,
      "currentWorkspaceIndex": 0,
      "customColorSchemes": [],
      "isCurated": false,
      "version": "31",
      "viewDensity": "expanded",
      "workspaces": [{
        "id": "3B1FE317-C72A-4983-ABF1-D3BD14A8D511",
        "name": "",
        "panels": [{
          "annotations": [],
          "collapsed": false,
          "dateRange": {
            "id": "thisMonth",
            "__entity__": true,
            "type": "DateRange",
            "__metaData__": {
              "name": "This month"
            }
          },
          "description": "",
          "id": "C0DACAE8-CCE1-4332-9496-ABDBE80C9C23",
          "name": "Freeform",
          "position": {
            "autoHeight": 374,
            "autoSize": true,
            "width": 100,
            "x": 0,
            "y": 0
          },
          "reportSuite": {
            "id": "dv_000000000000000000",
            "__entity__": true,
            "type": "ReportSuite",
            "__metaData__": {
              "name": "dv name",
              "rsid": "dv_000000000000000000"
            }
          },
          "segmentGroups": [],
          "subPanels": [{
            "collapsed": false,
            "description": "",
            "id": "F2AAAA88-E63E-4AB6-9090-3612F378B9F0",
            "isQuickInsightsSubPanel": false,
            "linkedSourceId": "",
            "position": {
              "autoHeight": 222,
              "autoSize": true,
              "width": 100,
              "x": 0,
              "y": 0
            },
            "reportlet": {
              "advancedMode": false,
              "advancedSettings": {
                "rows": [],
                "tableState": "builder"
              },
              "columnTree": {
                "_computedValues": [],
                "dataSettings": {
                  "advancedItemLimit": 5,
                  "advancedItemSearch": {
                    "operator": "AND",
                    "rules": []
                  }
                },
                "id": "f46df4-2",
                "name": "",
                "nodes": [{
                  "_computedValues": [],
                  "component": {
                    "id": "All_Visits",
                    "__entity__": true,
                    "type": "Segment",
                    "__metaData__": {
                      "name": "All Visits"
                    }
                  },
                  "dataSettings": {
                    "advancedItemLimit": 5,
                    "advancedItemSearch": {
                      "operator": "AND",
                      "rules": []
                    }
                  },
                  "id": "f46df4-4",
                  "name": "All Visits",
                  "nodes": [],
                  "selectionCoordinates": [],
                  "tableCellDisplay": {
                    "conditionalFormattingOpts": {
                      "autoGenerate": true,
                      "usePercentLimits": false
                    },
                    "location": "behindNumber",
                    "type": {
                      "anomaly": true,
                      "background": true,
                      "backgroundType": "bar",
                      "comparison": "none",
                      "interpretZeroAsNoValue": false,
                      "number": true,
                      "percent": true,
                      "showGrandTotal": true,
                      "showSparklines": true,
                      "showTotals": true,
                      "wrapHeaderText": true
                    }
                  }
                }],
                "selectionCoordinates": [],
                "tableCellDisplay": {
                  "conditionalFormattingOpts": {
                    "autoGenerate": true,
                    "usePercentLimits": false
                  },
                  "location": "behindNumber",
                  "type": {
                    "anomaly": true,
                    "background": true,
                    "backgroundType": "bar",
                    "comparison": "none",
                    "interpretZeroAsNoValue": false,
                    "number": true,
                    "percent": true,
                    "showGrandTotal": true,
                    "showSparklines": true,
                    "showTotals": true,
                    "wrapHeaderText": true
                  }
                }
              },
              "freeformTable": {
                "alignDatesForTimeDimension": true,
                "attributionSettings": [],
                "breakdowns": [],
                "collapsed": false,
                "columnWidths": [100, 100],
                "pagination": {
                  "currentPage": 0,
                  "viewBy": 50
                },
                "search": {
                  "operator": "AND",
                  "rules": []
                },
                "selectionCoordinates": [],
                "settings": {
                  "breakdownByPosition": false,
                  "rowBasedPercentages": false,
                  "totalsType": "columnSum"
                },
                "sort": {
                  "asc": false,
                  "columnId": "f46df4-4",
                  "labelColumn": false
                },
                "staticRows": [{
                  "component": {
                    "id": "metrics/visits",
                    "__entity__": true,
                    "type": "Metric",
                    "__metaData__": {
                      "name": "Visits"
                    }
                  },
                  "dataSettings": {
                    "advancedItemLimit": 5,
                    "advancedItemSearch": {
                      "operator": "AND",
                      "rules": []
                    }
                  },
                  "id": "f46df4-a"
                }],
                "statistics": {
                  "functions": [],
                  "ignoreZeros": true
                }
              },
              "isConfigVisible": true,
              "type": "FreeformReportlet"
            },
            "swatchColor": "#00C0C7",
            "type": "genericSubPanel",
            "visible": true,
            "visualizationIndex": 1
          }],
          "type": "panel"
        }]
      }]
    },
    "created": "2021-05-04T19:31:12Z"
  }
}

`}</code></pre>
    <h3 {...{
      "id": "example-response-4"
    }}>{`Example Response`}</h3>
    <p>{`The following response shows validation for the project with `}<inlineCode parentName="p">{`dataId`}</inlineCode>{`: `}<inlineCode parentName="p">{`dv_000000000000000000`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{}}>{`{
  "valid": true,
  "validatorVersion": "1.0.0"
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      